// src/components/shared/SearchBar.jsx
import React, { useCallback } from 'react';
import { Search, X } from 'lucide-react';

export const SearchBar = ({
  styling = {},
  headerSettings = {},
  actions = {},
  value = '', // ✅ Controlled value from parent
  onChange, // ✅ Direct onChange handler
  onSubmit // ✅ Direct submit handler
}) => {
  const [isFocused, setIsFocused] = React.useState(false);

  const showSearch = headerSettings.showSearch !== false;
  const showSearchOnMobile = headerSettings.showSearchOnMobile || false;
  const searchPosition = headerSettings.searchPosition || 'center';
  const searchPlaceholder = headerSettings.searchPlaceholder || 'Search...';
  const enableLiveSearch = headerSettings.enableLiveSearch !== false;

  const handleChange = useCallback((e) => {
    const newValue = e.target.value;
    
    // Call parent's onChange if provided
    if (onChange) {
      onChange(newValue);
    }
    
    // Also call actions.onSearchChange if live search is enabled
    if (enableLiveSearch && actions.onSearchChange) {
      actions.onSearchChange(newValue);
    }
  }, [onChange, enableLiveSearch, actions]);

  const handleSubmit = useCallback(() => {
    if (onSubmit) {
      onSubmit(value);
    } else if (actions.onSearchSubmit) {
      actions.onSearchSubmit(value);
    }
  }, [value, onSubmit, actions]);

  const handleClear = useCallback(() => {
    if (onChange) {
      onChange('');
    }
    if (actions.onSearchChange) {
      actions.onSearchChange('');
    }
  }, [onChange, actions]);

  if (!showSearch) return null;

  const mobileClass = showSearchOnMobile ? 'flex' : 'hidden md:flex';
  const positionClass = searchPosition === 'center' ? 'flex-1 max-w-2xl mx-4' : 'w-64';

  return (
    <div className={`${mobileClass} ${positionClass}`}>
      <div className="relative w-full">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
        <input
          type="text"
          placeholder={searchPlaceholder}
          value={value}
          onChange={handleChange}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              handleSubmit();
            }
          }}
          className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
          style={{
            borderColor: isFocused ? styling.primaryColor : undefined
          }}
        />
        {value && (
          <button
            onClick={handleClear}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
            type="button"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
};

export default SearchBar;